// components/HRSignupPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from "../components/Navbar";
import loginImage from "../assets/login-page.jpeg";
import api from '../utils/api';
import { getTenantFromUrl, handleTenantRedirect } from '../utils/tenantUtils';
import { trackEvent, setUserProperties } from '../utils/analytics';
const HRSignupPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'HR',
    otp: '',
    orgName: '',
    subdomain: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const tenant = getTenantFromUrl();

  // Registration on subdomains is blocked.
  // The redirect logic has been removed to display a friendly message instead.

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // SIGNUP validations
  const validateRequestOtpSignup = () => {
    let errs = {};
    if (!formData.email.trim()) errs.email = 'Email is required';
    if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
    if (!formData.phone.trim()) errs.phone = 'Phone number is required';
    
    if (!tenant) {
      if (!formData.orgName.trim()) errs.orgName = 'Organization name is required';
      if (!formData.subdomain.trim()) {
        errs.subdomain = 'Subdomain is required';
      } else if (!/^[a-z0-9-]+$/.test(formData.subdomain.trim())) {
        errs.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
      }
    }
    
    if (!formData.role) errs.role = 'Role is required';
    return errs;
  };

  const validateVerifyOtp = () => {
    let errs = {};
    if (!formData.otp.trim()) errs.otp = 'OTP is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    return errs;
  };

  const requestOtp = async () => {
    const errs = validateRequestOtpSignup();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return false;
    }

    setLoading(true);
    try {
      const body = {
        email: formData.email,
        name: formData.fullName,
        phone: formData.phone,
        role: formData.role,
        orgName: formData.orgName,
        subdomain: formData.subdomain,
      };

      await api.post('/api/auth/register', body);
      setOtpSent(true);
      setErrors({});
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message || 'Error requesting OTP';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
    return true;
  };

  const verifyOtp = async () => {
    const errs = validateVerifyOtp();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const body = {
        email: formData.email,
        otp: formData.otp,
        name: formData.fullName,
        phone: formData.phone,
        role: formData.role,
        orgName: formData.orgName,
        subdomain: formData.subdomain,
      };

      const response = await api.post('/api/auth/verify-otp', body);
      const data = response.data;
      if (data.user) {
        trackEvent('sign_up', {
          method: 'email_otp',
          role: data.user.role || 'HR',
        });
        setUserProperties({
          user_type: data.user.role || 'HR',
          org: data.user.org_name || '',
        });
        const redirected = handleTenantRedirect(data.user);
        if (!redirected) {
          localStorage.setItem('user', JSON.stringify(data.user));
          navigate('/interview');
        }
      } else {
        navigate('/interview');
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message || 'Error verifying OTP';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    if (!otpSent) {
      requestOtp();
    } else {
      verifyOtp();
    }
  };
  if (tenant) {
    return (
      <>
        <Navbar />
        <div style={styles.page}>
          <div style={{ ...styles.container, justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
            <div style={{
              background: 'var(--bg-card-glass)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '24px',
              padding: '40px',
              border: '1px solid var(--border-card)',
              boxShadow: 'var(--shadow-lg)',
              textAlign: 'center',
              maxWidth: '500px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#fee2e2',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                🔒
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', color: 'var(--text-primary)' }}>
                Registration Restricted
              </h2>
              <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '32px' }}>
                Self-service registration is disabled for <strong>{tenant}</strong>. 
                New team members must be added manually by the organization administrator.
              </p>
              <button 
                onClick={() => navigate('/login')}
                style={{
                  background: 'linear-gradient(135deg, #1976d2, #464feb)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 28px',
                  borderRadius: '30px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(70, 79, 235, 0.25)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(70, 79, 235, 0.35)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(70, 79, 235, 0.25)';
                }}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
  <>
    <Navbar />

    <div style={styles.page}>
      <div style={styles.container}>

        {/* LEFT SIDE - FORM */}
        <div style={styles.left}>
          <div style={styles.formBox}>
            <h2 style={styles.title}>Create Account</h2>
            <p style={styles.subtitle}>
              {!otpSent
                ? 'Create your HR account'
                : 'Enter OTP sent to your email'}
            </p>

            <form onSubmit={handleSubmit}>

              {!otpSent && (
                <>
                  {!tenant && (
                    <>
                      <input
                        type="text"
                        name="orgName"
                        placeholder="Organization Name (e.g. Infosys)"
                        value={formData.orgName}
                        onChange={handleChange}
                        style={styles.input}
                      />
                      {errors.orgName && <p style={styles.fieldError}>{errors.orgName}</p>}

                      <input
                        type="text"
                        name="subdomain"
                        placeholder="Desired Subdomain (e.g. infosys)"
                        value={formData.subdomain}
                        onChange={handleChange}
                        style={styles.input}
                      />
                      {errors.subdomain && <p style={styles.fieldError}>{errors.subdomain}</p>}
                    </>
                  )}
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleChange}
                    style={styles.input}
                  />
                  {errors.fullName && <p style={styles.fieldError}>{errors.fullName}</p>}

                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    style={styles.input}
                  />
                  {errors.email && <p style={styles.fieldError}>{errors.email}</p>}

                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    style={styles.input}
                  />
                  {errors.phone && <p style={styles.fieldError}>{errors.phone}</p>}
                </>
              )}

              {otpSent && (
                <>
                  <input
                    type="text"
                    name="otp"
                    placeholder="Enter OTP"
                    value={formData.otp}
                    onChange={handleChange}
                    style={styles.input}
                  />
                  {errors.otp && <p style={styles.fieldError}>{errors.otp}</p>}
                </>
              )}

              {errors.submit && (
                <p style={styles.error}>{errors.submit}</p>
              )}

              <button type="submit" style={styles.button}>
                {loading
                  ? 'Please wait...'
                  : otpSent
                  ? 'Verify OTP'
                  : 'Send OTP'}
              </button>

              <p style={styles.loginText}>
                Already have an account?{' '}
                <span onClick={() => navigate('/login')} style={styles.link}>
                  Login
                </span>
              </p>

            </form>
          </div>
        </div>

        {/* RIGHT SIDE - IMAGE */}
        <div style={styles.right}>
          <img
            src={loginImage}
            alt="AI"
            style={styles.image}
          />
         
        </div>

      </div>
    </div>
  </>
);
};
const styles = {
  page: {
    minHeight: 'calc(100vh - 72px)',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    background: 'var(--bg-page)',
    padding: '16px 14px',
  },

 container: {
  width: 'min(980px, 100%)',
  minHeight: '500px',
  maxHeight: 'min(640px, 92vh)',
  boxSizing: 'border-box',
  margin: '0 auto',
  display: 'flex',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: 'var(--shadow-lg)',
  border: '1px solid var(--border-light)',
  background: 'var(--bg-card)',
},

left: {
  flex: '0 0 43%',
  padding: '24px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-card)',
  overflowY: 'auto',
},

right: {
  flex: '1',
  position: 'relative',
},

 formBox: {
  width: '100%',
  maxWidth: '320px',
},
 title: {
  fontSize: '24px',
  fontWeight: '800',
  marginBottom: '6px',
  color: 'var(--text-primary)',
},

subtitle: {
  color: 'var(--text-tertiary)',
  marginBottom: '14px',
  fontSize: '12px',
},
input: {
  width: '100%',
  padding: '10px 11px',
  marginBottom: '5px',
  borderRadius: '9px',
  border: '1px solid var(--border-input)',
  fontSize: '12px',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
},

fieldError: {
  color: '#dc2626',
  fontSize: '12px',
  marginBottom: '10px',
  marginTop: '0',
},

button: {
  width: '100%',
  padding: '10px',
  background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '9px',
  cursor: 'pointer',
  fontWeight: '700',
  marginTop: '5px',
  boxShadow: '0 10px 20px rgba(29,78,216,0.25)',
},
  loginText: {
    marginTop: '10px',
    fontSize: '12px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },

  link: {
    color: '#818cf8',
    cursor: 'pointer',
    fontWeight: '600',
  },

  error: {
    color: '#dc2626',
    fontSize: '13px',
    marginBottom: '10px',
  },

  

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',  
  },

  overlayText: {
    position: 'absolute',
    bottom: '20px',
    left: '20px',
    color: '#fff',
    background: 'rgba(0,0,0,0.4)',
    padding: '15px',
    borderRadius: '8px',
  },
  
};

export default HRSignupPage;